import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSite } from '../hooks/useSites';
import { api } from '../api/client';
import { useToast } from '../hooks/useToast';
import { useBeginnerMode } from '../hooks/useBeginnerMode';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import PageToggles from '../components/PageToggles';
import { getTemplate } from '../data/templates';
import type { SiteStatus, ContentItem } from '../types';
import '../styles/editor.css';

function TemplateBadge({ templateId }: { templateId: string }) {
  const tmpl = getTemplate(templateId);
  return (
    <span className="tp-badge">
      <span className="tp-badge-swatches">
        <span className="tp-badge-swatch" style={{ background: tmpl.colors.primary }} />
        <span className="tp-badge-swatch" style={{ background: tmpl.colors.secondary }} />
        <span className="tp-badge-swatch" style={{ background: tmpl.colors.background, border: '0.5px solid #d1d5db' }} />
      </span>
      {tmpl.name}
    </span>
  );
}

function TemplateVersionBadge({ site }: { site: { templateId: string; templateVersion?: string } }) {
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getTemplateVersions(site.templateId)
      .then((data) => setLatestVersion(data.latest))
      .catch(() => setLatestVersion(null))
      .finally(() => setLoading(false));
  }, [site.templateId]);

  const currentVersion = site.templateVersion || 'latest';
  const hasUpdate = !loading && latestVersion && currentVersion !== 'latest' && currentVersion !== latestVersion;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span className="tag">{currentVersion}</span>
      {hasUpdate && (
        <span className="tag" style={{ background: 'var(--color-warning-bg)', color: '#92400e', fontSize: '0.75rem' }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg> {latestVersion} available
        </span>
      )}
    </span>
  );
}

export default function SiteOverview() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { site, loading, error, refresh } = useSite(siteId);
  const { addToast } = useToast();
  const { beginnerMode } = useBeginnerMode();

  const [editing, setEditing] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Storage usage state
  const [storageUsage, setStorageUsage] = useState<{
    usedBytes: number;
    usedMb: number;
    limitMb: number;
    percentUsed: number;
  } | null>(null);
  const [storageLoading, setStorageLoading] = useState(true);

  // Setup progress state
  const [setupProgress, setSetupProgress] = useState<{
    bannerReady: boolean;
    aboutReady: boolean;
    contactReady: boolean;
    productReady: boolean;
  }>({ bannerReady: false, aboutReady: false, contactReady: false, productReady: false });
  const [setupLoading, setSetupLoading] = useState(true);
  const [setupDismissed, setSetupDismissed] = useState(() => {
    if (!siteId) return false;
    return localStorage.getItem(`topzite-setup-dismissed-${siteId}`) === 'true';
  });

  // Load setup progress
  useEffect(() => {
    if (!siteId) {
      setSetupLoading(false);
      return;
    }
    setSetupLoading(true);
    Promise.all([
      api.listContent(siteId, 'hero').catch(() => [] as ContentItem[]),
      api.listContent(siteId, 'about').catch(() => [] as ContentItem[]),
      api.listContent(siteId, 'contact').catch(() => [] as ContentItem[]),
      api.listContent(siteId, 'product').catch(() => [] as ContentItem[]),
    ]).then(([heroes, abouts, contacts, products]) => {
      setSetupProgress({
        bannerReady: heroes.some((h) => !!(h.data?.headline)),
        aboutReady: abouts.some((a) => !!(a.data?.title)),
        contactReady: contacts.some((c) => !!(c.data?.email || c.data?.phone)),
        productReady: products.length > 0,
      });
    }).finally(() => setSetupLoading(false));
  }, [siteId]);

  useEffect(() => {
    if (!siteId) return;
    setStorageLoading(true);
    api
      .getStorageUsage(siteId)
      .then(setStorageUsage)
      .catch(() => setStorageUsage(null))
      .finally(() => setStorageLoading(false));
  }, [siteId]);

  if (loading) {
    return (
      <div className="page">
        <div className="loading-state">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="page">
        <div className="error-state">
          <p>{error || 'Site not found'}</p>
        </div>
      </div>
    );
  }

  const handleSaveName = async () => {
    if (!siteName.trim() || !siteId) return;
    setSaving(true);
    try {
      await api.updateSite(siteId, { siteName: siteName.trim() });
      addToast('success', 'Site name updated');
      setEditing(false);
      refresh();
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!siteId) return;
    setDeleting(true);
    try {
      await api.deleteSite(siteId);
      addToast('success', 'Site deleted');
      navigate('/dashboard');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  // SVG icon props (matching Sidebar style)
  const s = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

  const pageIcons = {
    hero:     <svg {...s}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/></svg>,
    about:    <svg {...s}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    products: <svg {...s}><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
    contact:  <svg {...s}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    gallery:  <svg {...s}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>,
    blog:     <svg {...s}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    publish:  <svg {...s}><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></svg>,
    edit:     <svg {...s}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    check:    <svg {...s}><polyline points="20 6 9 17 4 12"/></svg>,
  };

  // Calculate setup completeness
  const isLive = site.status === 'live';
  const setupItems = [
    { label: 'Add your homepage banner', done: setupProgress.bannerReady, path: `/sites/${siteId}/content/hero`, icon: pageIcons.hero },
    { label: 'Write your about section', done: setupProgress.aboutReady, path: `/sites/${siteId}/content/about`, icon: pageIcons.about },
    { label: 'Add your contact info', done: setupProgress.contactReady, path: `/sites/${siteId}/content/contact`, icon: pageIcons.contact },
    { label: 'Add your first product', done: setupProgress.productReady, path: `/sites/${siteId}/content/products`, icon: pageIcons.products },
    { label: 'Publish your site', done: isLive, path: `/sites/${siteId}/publish`, icon: pageIcons.publish },
  ];
  const doneCount = setupItems.filter((i) => i.done).length;
  const allDone = doneCount === setupItems.length;
  const progressPercent = Math.round((doneCount / setupItems.length) * 100);

  // All toggleable pages for the master toggle list
  const togglePages = [
    { field: 'showAbout', label: 'About', path: `/sites/${siteId}/content/about`, icon: pageIcons.about },
    { field: 'showProducts', label: 'Products / Services', path: `/sites/${siteId}/content/products`, icon: pageIcons.products },
    { field: 'showGallery', label: 'Gallery', path: `/sites/${siteId}/content/gallery`, icon: pageIcons.gallery },
    { field: 'showBlog', label: 'Blog', path: `/sites/${siteId}/content/blog`, icon: pageIcons.blog },
    { field: 'showContact', label: 'Contact', path: `/sites/${siteId}/content/contact`, icon: pageIcons.contact },
    { field: 'showTestimonials', label: 'Testimonials', path: `/sites/${siteId}/content/testimonials`, icon: pageIcons.edit },
    { field: 'showHours', label: 'Opening Hours', path: `/sites/${siteId}/content/hours`, icon: pageIcons.edit },
    { field: 'showMenu', label: 'Menu', path: `/sites/${siteId}/content/menu`, icon: pageIcons.edit },
  ];

  return (
    <div className="page">
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          {editing ? (
            <div className="inline-edit">
              <input
                type="text"
                className="field-input"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                autoFocus
              />
              <button className="btn btn-primary btn-sm" onClick={handleSaveName} disabled={saving}>
                Save
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <h1
              className="editable-title"
              onClick={() => {
                setSiteName(site.siteName);
                setEditing(true);
              }}
              title="Click to rename"
            >
              {site.siteName}
            </h1>
          )}
        </div>
        <div className="dashboard-header-actions">
          {site.siteUrl ? (
            <a
              href={site.siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> View Site
            </a>
          ) : (
            <button
              className="btn btn-secondary"
              onClick={() => addToast('info', 'Publish your site first to see it live')}
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> View Site
            </button>
          )}
        </div>
      </div>

      {/* ── Live site banner ── */}
      {site.siteUrl && (
        <div className="dashboard-live-banner">
          <div className="dashboard-live-banner-icon"><svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg></div>
          <div className="dashboard-live-banner-content">
            <strong>Your site is live!</strong>
            <a
              href={site.siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="dashboard-live-banner-url"
            >
              {site.siteUrl} ↗
            </a>
          </div>
          {site.lastDeployedAt && (
            <span className="dashboard-live-banner-date">
              Last published {new Date(site.lastDeployedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {/* ── Setup progress (shown until all done or dismissed) ── */}
      {!setupLoading && !allDone && (!setupDismissed || beginnerMode) && (
        <div className="dashboard-setup">
          <div className="dashboard-setup-header">
            <h2>Get your site ready</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="dashboard-setup-count">{doneCount} of {setupItems.length} done</span>
              {!beginnerMode && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setSetupDismissed(true);
                    localStorage.setItem(`topzite-setup-dismissed-${siteId}`, 'true');
                  }}
                  title="Hide setup guide"
                  style={{ padding: '2px 8px', fontSize: '0.8rem', lineHeight: 1.2 }}
                >
                  ✕ Hide
                </button>
              )}
            </div>
          </div>
          <div className="dashboard-setup-bar">
            <div
              className="dashboard-setup-bar-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="dashboard-setup-items">
            {setupItems.map((item) => (
              <div
                key={item.label}
                className={`dashboard-setup-item ${item.done ? 'done' : ''}`}
                onClick={() => navigate(item.path)}
              >
                <span className="dashboard-setup-item-check">
                  {item.done ? pageIcons.check : <span className="dashboard-setup-item-circle" />}
                </span>
                <span className="dashboard-setup-item-icon">{item.icon}</span>
                <span className="dashboard-setup-item-label">{item.label}</span>
                <span className="dashboard-setup-item-arrow">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Page visibility toggles ── */}
      <div className="dashboard-section">
        <div className="dashboard-section-header">
          <h2>Page Visibility</h2>
          <span className="text-muted" style={{ fontSize: '0.85rem' }}>Choose which pages appear on your site</span>
        </div>
        <div className="overview-card" style={{ marginTop: 0 }}>
          <PageToggles siteId={siteId!} pages={togglePages} />
        </div>
      </div>

      {/* ── Site info grid ── */}
      <div className="overview-grid">
        {/* Site Info Card */}
        <div className="overview-card">
          <h2>Site Details</h2>
          <dl className="info-list">
            <div className="info-item">
              <dt>Template</dt>
              <dd>
                <TemplateBadge templateId={site.templateId} />
              </dd>
            </div>
            <div className="info-item">
              <dt>Template Version</dt>
              <dd>
                <TemplateVersionBadge site={site} />
              </dd>
            </div>
            <div className="info-item">
              <dt>Status</dt>
              <dd><StatusBadge status={site.status as SiteStatus} /></dd>
            </div>
            <div className="info-item">
              <dt>Created</dt>
              <dd>{new Date(site.createdAt).toLocaleDateString()}</dd>
            </div>
            {site.lastDeployedAt && (
              <div className="info-item">
                <dt>Last Published</dt>
                <dd>{new Date(site.lastDeployedAt).toLocaleDateString()}</dd>
              </div>
            )}
          </dl>
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/sites/${siteId}/versions`)}>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Version History
            </button>
            {setupDismissed && !allDone && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setSetupDismissed(false);
                  localStorage.removeItem(`topzite-setup-dismissed-${siteId}`);
                }}
              >
                Show Setup Guide
              </button>
            )}
          </div>
        </div>

        {/* Domain Card */}
        <div className="overview-card">
          <h2>Custom Domain</h2>
          {site.customDomain ? (
            <p>
              <span className="domain-value">{site.customDomain}</span>
              {' — '}
              <span className="text-muted">{(site as unknown as Record<string, unknown>).domainStatus === 'active' ? 'Active' : 'Setup in progress'}</span>
            </p>
          ) : (
            <p className="text-muted">No custom domain yet. Use your own web address for a professional look.</p>
          )}
          <button
            className="btn btn-secondary btn-sm"
            style={{ marginTop: 8 }}
            onClick={() => navigate(`/sites/${siteId}/domain`)}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg> {site.customDomain ? 'Manage Domain' : 'Connect a Domain'}
          </button>
        </div>

        {/* Storage Card */}
        <div className="overview-card">
          <h2><svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg> Storage</h2>
          {storageLoading ? (
            <div className="loading-state" style={{ padding: '24px 0' }}>
              <div className="spinner" />
            </div>
          ) : storageUsage ? (
            <div>
              <div className="storage-bar-container">
                <div
                  className="storage-bar-fill"
                  style={{
                    width: `${Math.min(storageUsage.percentUsed, 100)}%`,
                    background:
                      storageUsage.percentUsed > 85
                        ? 'var(--color-error)'
                        : storageUsage.percentUsed > 60
                          ? 'var(--color-warning)'
                          : 'var(--color-success)',
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <span className="text-muted">
                  {storageUsage.usedMb} MB of {storageUsage.limitMb} MB used
                </span>
                <span
                  className="tag"
                  style={{
                    background:
                      storageUsage.percentUsed > 85
                        ? 'var(--color-error-bg)'
                        : storageUsage.percentUsed > 60
                          ? 'var(--color-warning-bg)'
                          : 'var(--color-success-bg)',
                    color:
                      storageUsage.percentUsed > 85
                        ? 'var(--color-error)'
                        : storageUsage.percentUsed > 60
                          ? '#92400e'
                          : '#065f46',
                  }}
                >
                  {storageUsage.percentUsed}%
                </span>
              </div>
            </div>
          ) : (
            <p className="text-muted">Unable to load storage usage.</p>
          )}
        </div>

      </div>

      {/* ── Danger Zone ── */}
      <div className="danger-zone">
        <h2>Danger Zone</h2>
        <p>Permanently delete this site and all its content. This cannot be undone.</p>
        <button
          className="btn btn-danger"
          onClick={() => setDeleteModalOpen(true)}
        >
          Delete Site
        </button>
      </div>

      {/* Delete Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteConfirmName('');
        }}
        title="Delete Site"
        actions={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setDeleteModalOpen(false);
                setDeleteConfirmName('');
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={deleting || deleteConfirmName.trim() !== site.siteName}
            >
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{site.siteName}</strong>? This action cannot be
          undone. All content, media, and published versions will be permanently removed.
        </p>
        <div className="form-field" style={{ marginTop: 12 }}>
          <label className="field-label" htmlFor="delete-confirm-name">
            Type <strong>{site.siteName}</strong> to confirm
          </label>
          <input
            id="delete-confirm-name"
            type="text"
            className="field-input"
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            placeholder={site.siteName}
            autoComplete="off"
          />
        </div>
      </Modal>

    </div>
  );
}
